import { useActions } from "app/utils/hooks";
import { useCallback, useEffect, useRef, useState } from "react";
import { shallowEqual, useSelector } from "react-redux";
import { RootState } from "typedefs";
import { clearApplyTemplate } from "app/actions/applyTemplate";
import SpaceModel from "app/models/SpaceModel";
import TeamGroupModel from "app/models/TeamGroupModel";
import TeamModel from "app/models/TeamModel";

export const useApplyTemplateDropdown = () => {
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [isSpaceSelectModalOpen, setSpaceSelectModalOpen] =
    useState<boolean>(false);

  const [isGroupSelectModalOpen, setGroupSelectModalOpen] =
    useState<boolean>(false);

  const [isChannelSelectModalOpen, setChannelSelectModalOpen] =
    useState<boolean>(false);

  const [successModal, setSuccessModal] = useState<boolean>(false);

  const [appliedTo, setAppliedTo] = useState<{
    location: string;
    applied: string[] | string;
  }>({
    location: `space`,
    applied: [],
  });

  const applyTemplateSuccess = useSelector(
    (state: RootState) => state.applyTemplate.success,
    shallowEqual,
  );

  const applyTemplateActions = useActions({
    clearApplyTemplate,
  });

  const space = useSelector(
    (state: RootState) => state.applyTemplate.spaceObj,
    shallowEqual,
  );
  const groupObj = useSelector(
    (state: RootState) => state.applyTemplate.groupObj,
    shallowEqual,
  );
  const channel = useSelector(
    (state: RootState) => state.applyTemplate.groupChannelObj,
    shallowEqual,
  );

  const applyTemplateError = useSelector(
    (state: RootState) => state.applyTemplate.errorApplyTemplate,
    shallowEqual,
  );

  const teamFetchError = useSelector((state: RootState) => state.team.error);

  const [teamsFetchFailed, setTeamsFetchFailed] = useState<boolean>(false);

  const clearTeamsFetchFailed = useCallback(() => {
    setTeamsFetchFailed(false);
  }, []);

  // The team slice is global, so an error can already be sitting there when a
  // modal opens; only a NEW error (set by the open modal's own fetch) should
  // close it and raise the toast.
  const prevTeamErrorRef = useRef(teamFetchError);

  useEffect(() => {
    const errorChanged = teamFetchError !== prevTeamErrorRef.current;
    prevTeamErrorRef.current = teamFetchError;

    if (!errorChanged || !teamFetchError) {
      return;
    }

    if (isGroupSelectModalOpen) {
      setGroupSelectModalOpen(false);
      setTeamsFetchFailed(true);
    } else if (isChannelSelectModalOpen) {
      setChannelSelectModalOpen(false);
      setTeamsFetchFailed(true);
    }
  }, [teamFetchError, isGroupSelectModalOpen, isChannelSelectModalOpen]);

  const handleSpaceSelectModal = useCallback(() => {
    setSpaceSelectModalOpen((prev) => !prev);

    if (!isSpaceSelectModalOpen) {
      applyTemplateActions.clearApplyTemplate();
    }
  }, [isSpaceSelectModalOpen]);

  const handleGroupSelectModal = useCallback(() => {
    setGroupSelectModalOpen((prev) => !prev);

    if (!isGroupSelectModalOpen) {
      applyTemplateActions.clearApplyTemplate();
    }
  }, [isGroupSelectModalOpen]);

  const handleChannelSelectModal = useCallback(() => {
    setChannelSelectModalOpen((prev) => !prev);

    if (!isChannelSelectModalOpen) {
      applyTemplateActions.clearApplyTemplate();
    }
  }, [isChannelSelectModalOpen]);

  const appliedLocationHandler = useCallback(() => {
    if (space && space.length > 0) {
      const appliedStrings = space.map((item: SpaceModel) => item.athleteName);
      const locationString = `space${space.length > 1 ? "s" : ""}`;
      setAppliedTo({
        applied: formatAppliedStrings(appliedStrings),
        location: locationString,
      });
    } else if (groupObj && groupObj.length > 0) {
      const appliedStrings = groupObj.map((item: TeamModel) => item.name);
      const locationString = `group${groupObj.length > 1 ? "s" : ""}`;
      setAppliedTo({
        applied: formatAppliedStrings(appliedStrings),
        location: locationString,
      });
    } else if (channel && channel.length > 0) {
      const appliedStrings = channel.map((item: TeamGroupModel) => item.name);
      const locationString = `channel${channel.length > 1 ? "s" : ""}`;
      setAppliedTo({
        applied: formatAppliedStrings(appliedStrings),
        location: locationString,
      });
    } else {
      setAppliedTo({ ...appliedTo, location: null });
    }
  }, [space, groupObj, channel]);

  const formatAppliedStrings = (appliedStrings: string[]) => {
    if (appliedStrings.length <= 1) {
      return appliedStrings.join("");
    } else {
      const formattedString = appliedStrings.reduce(
        (accumulator, currentString, index) => {
          if (index === appliedStrings.length - 1) {
            return accumulator + currentString;
          } else {
            return accumulator + currentString + ", ";
          }
        },
        "",
      );
      return formattedString;
    }
  };

  const onCloseSuccessModal = useCallback(() => {
    setSuccessModal((prev) => !prev);
    applyTemplateActions.clearApplyTemplate();
  }, []);

  useEffect(() => {
    if (applyTemplateSuccess === true || applyTemplateError) {
      appliedLocationHandler();
      setSuccessModal((prev) => !prev);
    }
  }, [applyTemplateSuccess, applyTemplateError]);

  const { location, applied } = appliedTo;

  return {
    onCloseSuccessModal,
    successModal,
    location,
    applied,
    dropdownRef,
    isSpaceSelectModalOpen,
    isGroupSelectModalOpen,
    isChannelSelectModalOpen,
    handleSpaceSelectModal,
    handleGroupSelectModal,
    handleChannelSelectModal,
    teamsFetchFailed,
    clearTeamsFetchFailed,
  };
};
